import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";

import { formatTimestamp } from "@/packages/dashboard/utils/format-timestamp";
import MealItemsTable from "./MealItemsTable";
import type { DayGroup } from "./macro-aggregations";
import { formatMacroValue } from "./macro-aggregations";
import styles from "../with-bg-on-hover.module.css";

const formatMealMacroSummary = (totals: DayGroup["totals"]) =>
  `${formatMacroValue(totals.calories)} kcal · ${formatMacroValue(totals.proteins)}g protein · ${formatMacroValue(
    totals.carbs,
  )}g carbs · ${formatMacroValue(totals.fats)}g fat`;

const formatMealCountLabel = (count: number) => `${count} ${count === 1 ? "meal" : "meals"}`;

export type MealDetailsAccordionProps = {
  dayGroups: DayGroup[];
  dailyCalorieTarget?: number | null;
};

export const MealDetailsAccordion = ({ dayGroups, dailyCalorieTarget }: MealDetailsAccordionProps) => {
  if (dayGroups.length === 0) {
    return null;
  }

  return (
    <Stack gap="md" mt={"xl"} pt={"xl"}>
      <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
        Meal journal
      </Text>
      <Stack gap="lg">
        {dayGroups.map((day) => (
          <Stack key={day.key} gap={8}>
            <Group justify="space-between" align="baseline">
              <Stack gap={0}>
                <Text fw={600} style={{ color: "#111827" }}>
                  {day.label}
                </Text>
                <Text size="xs" style={{ color: "#4B5563" }}>
                  {formatMealCountLabel(day.meals.length)} · {formatMacroValue(day.totals.calories)} kcal total
                </Text>
              </Stack>
            </Group>

            <Stack gap="sm">
              {day.meals.map(({ meal, totals }) => {
                const macroSummary = formatMealMacroSummary(totals);
                const mealTime = formatTimestamp(meal.timestamp, {
                  formatOptions: { hour: "numeric", minute: "2-digit" },
                });

                return (
                  <Paper
                    key={meal.id}
                    component={Link}
                    to={`/add-meals/${meal.id}`}
                    withBorder
                    radius="md"
                    p="md"
                    className={styles.withBgOnHover}
                    style={{
                      borderColor: "#E5E7EB",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    <Stack gap={8}>
                      <Stack gap={2}>
                        <Text fw={600} size="sm" style={{ color: "#111827" }}>
                          {mealTime}
                        </Text>
                        <Text size="xs" style={{ color: "#4B5563" }}>
                          {meal.meal_items.length} {meal.meal_items.length === 1 ? "item" : "items"} · {macroSummary}
                        </Text>
                      </Stack>

                      <Stack gap={6}>
                        <MealItemsTable meal={meal} dailyCalorieTarget={dailyCalorieTarget} />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
